import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { ArcheProvider } from '../providers/ArcheProvider';
import { ArcheLayout, ArcheDisplayMode } from '../components/ArcheLayout';
import { ArcheHeader } from '../components/ArcheHeader';
import { useArcheContext } from '@/sdui/hooks/useArcheContext';
import { PageRenderer } from '@/sdui/renderer/PageRenderer';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { MakeARequestPage } from './make-a-request';
import { SingleAIPage } from './single-ai';
import { ImageGeneratorArchePage } from './image-generator';
import { MultiAIPage } from './multi-ai';
import { LearnAboutFiasChatPage } from './learn-fias-chat';
import { LearnAnythingPage } from './learn-anything';
import { ContactBookArchePage } from './contact-book';
import { ChatArchePage } from './chat';
import { GuildArchePage } from './guilds';
import { DfeArchePage } from './dfe';
import { DfeInventoryTransitionArchePage } from './dfe-inventory-transition';
import { BuilderArchePage } from './builder';
import { FavoritesArchePage } from './favorites';
import { GameRewardsArchePage } from './game-rewards';
import { DfeGameStateArchePage } from './dfe-game-state';
import { PollsArchePage } from './polls';
import { PluginBuilderArchePage } from './plugin-builder';
import { PluginRenderer } from '../components/PluginRenderer';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';

function ArcheContent({
  archeId,
  displayMode = 'normal',
}: {
  archeId: string;
  displayMode?: ArcheDisplayMode;
}) {
  const { ui, currentPage, isLoading, error } = useArcheContext();

  if (isLoading) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4 mb-6" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ui || !currentPage) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Not Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">This arche could not be loaded.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const page = ui.pages[currentPage];

  if (!page) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Page Not Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              The requested page &quot;{currentPage}&quot; was not found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get arche title from UI metadata or use a formatted archeId
  const archeTitle =
    ui.metadata?.name ||
    archeId
      .replace('arche_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="flex flex-col h-full">
      {/* Hide ArcheHeader in fullscreen and embed modes */}
      {displayMode !== 'fullscreen' && displayMode !== 'embed' && (
        <ArcheHeader archeId={archeId} title={archeTitle} />
      )}
      <div className="flex-1 overflow-auto p-8">
        <PageRenderer page={page} />
      </div>
    </div>
  );
}

/**
 * Determines the display mode for the arche layout based on metadata, auth state, and embed flag.
 *
 * Security: Embed mode is only allowed for arches that are:
 * - Public (isPublic=true)
 * - Read-only (no workflow)
 * This prevents authenticated actions from being performed in embedded iframes.
 */
function determineDisplayMode(
  metadata: ArcheMetadata | null,
  isAuthenticated: boolean,
  isEmbed: boolean,
): ArcheDisplayMode {
  // Embed mode is only allowed for embeddable arches (public + no workflow)
  // This is a security measure to prevent authenticated actions in iframes
  if (isEmbed && metadata?.isEmbeddable) {
    return 'embed';
  }

  if (!metadata) {
    return 'normal'; // Default to normal while loading
  }

  // Full-screen mode takes precedence
  if (metadata.isFullScreen) {
    return 'fullscreen';
  }

  // Public mode for unauthenticated users on public arches
  if (metadata.isPublic && !isAuthenticated) {
    return 'public';
  }

  return 'normal';
}

/**
 * Error component shown when embed mode is requested but not allowed
 */
function EmbedNotAllowedError() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Card className="max-w-md mx-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Embedding Not Allowed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            This arche cannot be embedded. Only public, read-only arches can be embedded in iframes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Generic arche page that loads UI via SDUI and uses the ArcheLayout
 */
function GenericArchePage({ archeId }: { archeId: string }) {
  const { isAuthenticated, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [metadata, setMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);

  // Check for embed mode via query parameter
  const isEmbed = searchParams.get('embed') === 'true';

  // Security: Clear any existing authentication when in embed mode
  // This prevents authenticated actions from being performed in embedded iframes
  useEffect(() => {
    if (isEmbed && isAuthenticated) {
      signOut();
    }
  }, [isEmbed, isAuthenticated, signOut]);

  useEffect(() => {
    let cancelled = false;

    async function fetchMetadata() {
      try {
        const result = await api.getArcheMetadata(archeId);
        if (!cancelled) {
          setMetadata(result);
        }
      } catch {
        // If metadata fetch fails, use normal mode
        if (!cancelled) {
          setMetadata(null);
        }
      } finally {
        if (!cancelled) {
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  // Check if embed mode was requested but not allowed
  const embedDenied = isEmbed && !metadataLoading && metadata && !metadata.isEmbeddable;

  // If embed mode requested but not allowed, show error
  if (embedDenied) {
    return <EmbedNotAllowedError />;
  }

  // Plugin arches render in a sandboxed iframe instead of SDUI
  if (!metadataLoading && metadata?.renderMode === 'plugin') {
    return (
      <ArcheLayout displayMode="normal">
        <PluginRenderer archeId={archeId} />
      </ArcheLayout>
    );
  }

  const displayMode = metadataLoading
    ? isEmbed
      ? 'embed'
      : 'normal'
    : determineDisplayMode(metadata, isAuthenticated, isEmbed);

  return (
    <ArcheProvider archeId={archeId}>
      <ArcheLayout displayMode={displayMode}>
        <ArcheContent archeId={archeId} displayMode={displayMode} />
      </ArcheLayout>
    </ArcheProvider>
  );
}

export function ArcheInteractionPage() {
  const { id: archeId } = useParams<{ id: string }>();

  if (!archeId) {
    return <Navigate to="/a/arche_make_a_request" replace />;
  }

  // Redirects
  if (archeId === 'arche_marketplace') {
    return <Navigate to="/marketplace" replace />;
  }
  if (archeId === 'arche_builder') {
    return <BuilderArchePage />;
  }
  // Internal arches with dedicated page components
  if (archeId === 'arche_guilds') {
    return <GuildArchePage />;
  }
  if (archeId === 'arche_chat') {
    return <ChatArchePage />;
  }
  if (archeId === 'arche_make_a_request') {
    return <MakeARequestPage />;
  }
  if (archeId === 'arche_single_ai') {
    return <SingleAIPage />;
  }
  if (archeId === 'arche_image_generator') {
    return <ImageGeneratorArchePage />;
  }
  if (archeId === 'arche_multi_ai') {
    return <MultiAIPage />;
  }
  if (archeId === 'arche_learn_fias_chat') {
    return <LearnAboutFiasChatPage />;
  }
  if (archeId === 'arche_learn_anything') {
    return <LearnAnythingPage />;
  }
  if (archeId === 'arche_dfe') {
    return <DfeArchePage />;
  }
  if (archeId === 'arche_dfe_inventory_transition') {
    return <DfeInventoryTransitionArchePage />;
  }
  if (archeId === 'arche_contact_book') {
    return <ContactBookArchePage />;
  }
  if (archeId === 'arche_favorites') {
    return <FavoritesArchePage />;
  }
  if (archeId === 'arche_game_rewards') {
    return <GameRewardsArchePage />;
  }
  if (archeId === 'arche_dfe_game_state') {
    return <DfeGameStateArchePage />;
  }
  if (archeId === 'arche_polls') {
    return <PollsArchePage />;
  }
  if (archeId === 'arche_plugin_builder') {
    return <PluginBuilderArchePage />;
  }

  // All other arches use the generic SDUI renderer
  return <GenericArchePage archeId={archeId} />;
}

export default ArcheInteractionPage;
